import { subscribeAuthTokenChanges } from './utils/authSession';
// react-hot-loader removed
import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
// import './screens/Landing/assets/scss/style.scss';
import './index.scss';
import './appearance/Fonts/inter.css';
import App from './App';
import ErrorBoundary from './components/ErrorComponents/ErrorBoundary';
import * as serviceWorker from './serviceWorker';
import 'react-loader-spinner/dist/loader/css/react-spinner-loader.css';
import { createProductAnalyticsSession } from './util/productAnalyticsSession';
import environment from './env-config';
import mixpanel from './util/mixpanelClient';
import { preloadMarketingPages } from './data/marketingPagesLoader';
import { MARKETING_PAGE_META } from './data/marketingPageMeta';

function AppBootstrap() {
    React.useLayoutEffect(() => {
        if (window.__clearLuckynoteStartupOverlay) {
            window.__clearLuckynoteStartupOverlay();
        } else {
            document.documentElement.classList.remove('app-shell-pending');
        }
    }, []);

    return (
        <ErrorBoundary>
            <App />
        </ErrorBoundary>
    );
}

const IS_PRERENDER =
    typeof navigator !== 'undefined' &&
    (
        navigator.userAgent === 'ReactSnap' ||
        navigator.userAgent.includes('ReactSnap') ||
        (typeof window !== 'undefined' && typeof window.snapSaveState === 'function')
    );
const parseReplayPercent = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
        return 0;
    }
    return Math.min(Math.max(parsed, 0), 100);
};

const getMixpanelConfig = ({ debug = false } = {}) => ({
    debug,
    ignore_dnt: true,
    record_sessions_percent: 0,
    record_mask_all_text: true,
    record_mask_all_inputs: true,
    record_block_selector: 'img, video, canvas, iframe',
    record_heatmap_data: false,
    record_network: false
});

const maybeStartMixpanelSessionReplay = (isCurrent) => {
    if (environment.REACT_NODE_ENV !== 'production') {
        return;
    }

    const replayPercent = parseReplayPercent(environment.REACT_MIXPANEL_SESSION_REPLAY_PERCENT);
    if (replayPercent <= 0 || Math.random() * 100 >= replayPercent) {
        return;
    }

    try {
        if (typeof mixpanel.start_session_recording === 'function') {
            mixpanel.start_session_recording_if(isCurrent);
        }
    } catch (error) {
        console.warn('Mixpanel session replay start failed:', error);
    }
};

// ReactDOM.render(
//   <React.StrictMode>
//     <App />
//   </React.StrictMode>,
//   document.getElementById('root')
// );

// Mixpanel is reserved for signed-in product analytics. It is deferred so the
// product analytics chunk never competes with the public site's initial paint.
const initMixpanel = (isCurrent) => {
    if (IS_PRERENDER) {
        return;
    }
    try {
        mixpanel.init_if(isCurrent, environment.REACT_MIXPANEL_TOKEN, getMixpanelConfig({
            debug: environment.REACT_NODE_ENV !== 'production'
        }));

        // Super property so every event is segmentable by platform
        const appPlatform = window.__LUCKYNOTE_TAURI__
            ? 'desktop'
            : window.matchMedia && window.matchMedia('(display-mode: standalone)').matches
                ? 'pwa'
                : 'web';
        mixpanel.register({
            app_platform: appPlatform,
            platform: appPlatform,
            client_type: 'web',
        });
    } catch (error) {
        console.warn('Failed to initialize Mixpanel:', error);
        // Continue without Mixpanel if initialization fails
    }
};

// The product analytics initialization is scheduled after load/idle, or on
// first interaction. Anonymous public visitors never call this function.
const runAnalyticsInitWhenIdle = (callback) => {
    const runAndMarkReady = () => callback();

    const scheduleIdleRun = () => {
        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(runAndMarkReady, { timeout: 2000 });
        } else {
            setTimeout(runAndMarkReady, 1);
        }
    };

    if (typeof window === 'undefined') {
        scheduleIdleRun();
        return;
    }

    let fired = false;
    const interactionEvents = ['pointerdown', 'keydown', 'scroll'];
    const runOnce = () => {
        if (fired) return;
        fired = true;
        interactionEvents.forEach(evt => window.removeEventListener(evt, runOnce));
        scheduleIdleRun();
    };

    interactionEvents.forEach(evt => window.addEventListener(evt, runOnce, { passive: true, once: true }));

    const afterLoadIdleDelay = () => setTimeout(runOnce, 3000);

    if (document.readyState === 'complete') {
        afterLoadIdleDelay();
    } else {
        window.addEventListener('load', afterLoadIdleDelay, { once: true });
    }
};

const startProductAnalytics = createProductAnalyticsSession({
    enabled: !IS_PRERENDER && environment.REACT_NODE_ENV === 'production',
    getToken: () => localStorage.getItem('token'),
    schedule: runAnalyticsInitWhenIdle,
    initialize: initMixpanel,
    start: maybeStartMixpanelSessionReplay,
    stop: () => mixpanel.stop_session_recording()
});

subscribeAuthTokenChanges(startProductAnalytics);

// Only signed-in users load product analytics. Anonymous visitors receive
// Umami-only public measurement from index.html.
startProductAnalytics(localStorage.getItem('token'));

if (typeof window !== 'undefined') {
    window.addEventListener('error', event => {
        if (window.__showLuckynoteStartupError && document.documentElement.classList.contains('app-shell-pending')) {
            const message = event?.error?.stack || event?.error?.message || event?.message || 'Unknown runtime error';
            window.__showLuckynoteStartupError(message);
        }
    });

    window.addEventListener('unhandledrejection', event => {
        if (window.__showLuckynoteStartupError && document.documentElement.classList.contains('app-shell-pending')) {
            const reason = event?.reason;
            const message =
                (typeof reason === 'string' && reason) ||
                reason?.stack ||
                reason?.message ||
                'Unknown promise rejection';
            window.__showLuckynoteStartupError(message);
        }
    });
}

// ReactGA.exception({
//     description: 'An error ocurred',
//     fatal: true
//   });

const container = document.getElementById('root');

// react-snap only ever prerenders the logged-out view of "/", "/login", and
// "/register" (there's no token at build time). Hydrating that shell always
// requires the first client render to match it exactly - i.e. start
// logged-out - so a returning logged-in visitor briefly sees the landing
// page before the app swaps in. We know synchronously, before mounting,
// whether this specific visitor has a token, so skip hydration entirely for
// them: fall back to a fresh createRoot render, which replaces the
// prerendered DOM outright instead of needing to match it, and expose the
// token check so Routes.js can render the authenticated view from its very
// first paint with no flash. See Routes.js's useHasTokenAfterMount.
const hasTokenAtBoot = Boolean(localStorage.getItem('token'));
window.__LUCKYNOTE_INITIAL_HAS_TOKEN__ = hasTokenAtBoot;

const shouldHydratePrerenderedShell = Boolean(
    container &&
    container.hasChildNodes() &&
    !document.documentElement.classList.contains('app-shell-pending') &&
    !hasTokenAtBoot
);

// Marketing pages (/compare/*, /for/*, /use-cases/*, /message-yourself/*,
// /download-app, ...) keep their large content data module out of the main
// bundle (see data/marketingPagesLoader.js). Their prerendered HTML contains
// the full page content, so hydration's first render must be able to produce
// that content synchronously - which means the data chunk has to finish
// loading BEFORE React mounts on those routes. For every other route this
// resolves immediately and mounting is unchanged.
const bootPathname =
    (window.location.pathname || '/').replace(/\/+$/, '') || '/';
const isMarketingRouteAtBoot = MARKETING_PAGE_META.some(
    (page) => page.path === bootPathname
);

const mountApp = () => {
    if (shouldHydratePrerenderedShell) {
        hydrateRoot(container, <AppBootstrap />);
    } else {
        const root = createRoot(container);
        root.render(
            <AppBootstrap />
        );
    }
};

if (isMarketingRouteAtBoot) {
    // Mount even if the chunk fails to load (MarketingPage falls back to
    // loading it itself and rendering a blank shell meanwhile).
    preloadMarketingPages().catch(() => {}).then(mountApp);
} else {
    mountApp();
}

const activateServiceWorkerUpdate = registration => {
    const waitingWorker = registration?.waiting;
    if (!waitingWorker) {
        return;
    }

    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
};

const PAGE_LOAD_KEY = 'luckynote_page_loaded_at';
const UPDATE_BANNER_THRESHOLD_MS = 24 * 60 * 60 * 1000; // 24h

const notifyServiceWorkerUpdateAvailable = registration => {
    if (typeof window === 'undefined' || !registration?.waiting) {
        return;
    }

    const pageLoadedAt = parseInt(localStorage.getItem(PAGE_LOAD_KEY) || '0', 10);
    const pageAge = Date.now() - pageLoadedAt;
    if (pageAge < UPDATE_BANNER_THRESHOLD_MS) {
        return;
    }

    window.__luckynoteServiceWorkerUpdateRegistration = registration;
    window.__activateLuckynoteServiceWorkerUpdate = () => activateServiceWorkerUpdate(registration);
    window.dispatchEvent(new CustomEvent('luckynote:service-worker-update'));
};

const showWaitingServiceWorkerUpdate = () => {
    if (!navigator.serviceWorker?.getRegistration) {
        return;
    }

    navigator.serviceWorker.getRegistration().then(registration => {
        if (registration?.waiting) {
            notifyServiceWorkerUpdateAvailable(registration);
        }
    }).catch(() => {});
};

if (!IS_PRERENDER && typeof localStorage !== 'undefined') {
    localStorage.setItem(PAGE_LOAD_KEY, String(Date.now()));
}

if (!IS_PRERENDER && 'serviceWorker' in navigator) {
    serviceWorker.register({
        onUpdate: registration => {
            notifyServiceWorkerUpdateAvailable(registration);
        }
    });
    window.addEventListener('load', showWaitingServiceWorkerUpdate);
} else {
    serviceWorker.unregister();
}
